import * as React from 'react';
import cn from 'classnames';
import { X } from 'src/common/icons';

import styles from './FiltersDrawerHeader.module.scss';

type FiltersDrawerHeaderProps = {
  /**
   * Closes the drawer. Wire to `useFiltersDrawer().closeIfMobile` or
   * directly to the page's `setFiltersVisible(false)` setter — both work,
   * but `closeIfMobile` is preferred so this stays a no-op if the same
   * code path is ever exercised on desktop.
   */
  onClose: () => void;
  /**
   * Optional override for the drawer title. Defaults to "Filters" which
   * is correct for Assets / Collections / Shares. Pass a different
   * string if a future drawer hosts something other than a filter rail.
   */
  title?: string;
  className?: string;
};

/**
 * Sticky header for the mobile filter drawer on the Assets, Collections,
 * and Shares list pages. Surfaces a visible Close (×) affordance so
 * users on phones aren't left with "tap the dark area" as the only way
 * out of the drawer.
 *
 * Visibility is driven entirely by CSS — the component renders on every
 * page that includes it, but `display: none` above 768px keeps it out
 * of the desktop layout where the filter rail sits inline.
 */
export const FiltersDrawerHeader: React.FC<FiltersDrawerHeaderProps> = ({
  onClose,
  title = 'Filters',
  className,
}) => (
  <div className={cn(styles.header, className)} data-test="filters-drawer-header">
    <span className={styles.title}>{title}</span>
    <button
      type="button"
      className={styles.closeButton}
      onClick={onClose}
      aria-label={`Close ${title.toLowerCase()}`}
      data-test="filters-drawer-close"
    >
      <X />
    </button>
  </div>
);

export default FiltersDrawerHeader;
